import { KmxArrowRightStroked } from '@kmx/mui-icons';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { formatCurrency } from '../../../helpers/formatters';
import InfoButton from '../../../shared/InfoButton';
import type { CalculatorInformation } from '../../../types/CalculatorInformation';
import type { FbsDecision } from '../../../types/FbsDecision';

type CalculatorTermsProps = {
  terms: CalculatorInformation;
  fbsDecision?: FbsDecision;
  pageName: string;
  stockNumber: string;
  isComingSoon: boolean;
};

const format = (value: number) => {
  return formatCurrency(Math.max(0, value));
};

const Flag = styled(Box)(({ theme }) => ({
  borderRadius: 8,
  backgroundColor: theme.palette.primary.faint,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,

  svg: { color: theme.palette.primary.main },

  '.kmx-car-tile--unavailable &': {
    backgroundColor: theme.palette.secondary.faint,

    span: { color: theme.palette.text.secondary },

    svg: { color: theme.palette.text.secondary },
  },
}));

const BudgetCalculatorTerms = ({
  terms,
  pageName,
  stockNumber,
  isComingSoon,
  fbsDecision,
}: CalculatorTermsProps) => {
  const {
    monthlyPayment,
    minMonthlyPayment,
    maxMonthlyPayment,
    onPrequalFlagClick,
    openPaymentSettingsModal,
    isEstimated = false,
  } = terms;
  const prequalAppLink = `/pre-qual/app?stockNumber=${stockNumber}&op=${pageName}`;
  const decisionIsApprovedOrCountered =
    fbsDecision && ['approved', 'countered'].includes(fbsDecision.decision);

  const formattedMonthlyPayment = useMemo(() => {
    if (minMonthlyPayment && maxMonthlyPayment) {
      return `${format(minMonthlyPayment)} - ${format(maxMonthlyPayment)}`;
    }

    if (monthlyPayment) {
      return format(monthlyPayment);
    }
  }, [monthlyPayment, minMonthlyPayment, maxMonthlyPayment]);

  return (
    <Box className="kmx-car-tile__post-calculator-terms">
      {!fbsDecision || (!isComingSoon && decisionIsApprovedOrCountered) ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.25}
          mb={isEstimated ? 1 : undefined}
        >
          <Typography variant="h6" component="span">
            {isEstimated ? 'Est. ' : null}
            {formattedMonthlyPayment}/mo
          </Typography>
          <InfoButton
            onClick={openPaymentSettingsModal}
            aria-label="more info about monthly payment"
            iconSize={18}
            variant="stroked"
          />
        </Stack>
      ) : (
        <>&#8288;</>
      )}

      {isEstimated ? null : (
        <Typography variant="caption" color="textSecondary">
          See personalized financing terms
        </Typography>
      )}

      <Flag className="kmx-car-tile__prequal-vsa">
        <Link
          href={prequalAppLink}
          onClick={onPrequalFlagClick}
          data-ignore-anchor-tag-rule
          underline="none"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="kmx-car-tile__prequal-vsa__label-text"
          >
            <Typography variant="caption" lineHeight="normal">
              {isEstimated
                ? 'Get pre-qualified to see your personalized monthly payment'
                : 'Get pre-qualified with no impact to credit'}
            </Typography>
            <KmxArrowRightStroked fontSize="inherit" />
          </Stack>
        </Link>
      </Flag>
    </Box>
  );
};

export default BudgetCalculatorTerms;
